import type { SpotDetail } from "@divespots/contracts";
import { CircleCheck, CloudDownload, TriangleAlert } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../components/Button.tsx";
import { formatBytes } from "../i18n/format.ts";
import { useLocale } from "../i18n/index.ts";
import { SaveError, estimateFor, removeSpot, saveSpot } from "./saver.ts";
import type { SaveProgress } from "./saver.ts";
import { idbStore } from "./store.ts";
import { offlineSupported, useSavedSpots } from "./useSavedSpots.ts";

type State =
  | { kind: "idle" }
  | { kind: "confirm" }
  | { kind: "saving"; progress: SaveProgress }
  | { kind: "error"; error: "quota" | "network" | "failed" };

async function persistStorage(): Promise<void> {
  try {
    await navigator.storage?.persist?.();
  } catch {
    /* a refusal is fine: the data is just evictable under pressure */
  }
}

/** Does the browser have room for `bytes` more? Unknown counts as yes. */
async function hasRoom(bytes: number): Promise<boolean> {
  try {
    const { quota, usage } = (await navigator.storage?.estimate?.()) ?? {};
    return quota === undefined || usage === undefined || quota - usage > bytes * 1.1;
  } catch {
    return true;
  }
}

/** "Offline speichern" in the spot sheet: estimate first, then progress with cancel, then "saved". */
export function OfflineSaveControl({ spot }: { spot: SpotDetail }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const { spots } = useSavedSpots();
  const saved = spots.find((s) => s.slug === spot.slug);
  const [state, setState] = useState<State>({ kind: "idle" });
  const abort = useRef<AbortController | null>(null);
  useEffect(() => () => abort.current?.abort(), []);

  if (!offlineSupported()) return null;

  const estimate = estimateFor(spot);

  const start = async () => {
    if (!(await hasRoom(estimate.bytes))) return setState({ kind: "error", error: "quota" });
    const ctrl = new AbortController();
    abort.current = ctrl;
    setState({ kind: "saving", progress: { done: 0, total: 1, bytes: 0 } });
    try {
      await saveSpot(
        spot,
        { store: idbStore, beforeWrite: persistStorage },
        ctrl.signal,
        (progress) => setState({ kind: "saving", progress }),
      );
      setState({ kind: "idle" });
    } catch (e) {
      if (ctrl.signal.aborted) return setState({ kind: "idle" });
      setState({ kind: "error", error: e instanceof SaveError ? e.kind : "failed" });
    } finally {
      abort.current = null;
    }
  };

  if (state.kind === "saving") {
    const { done, total } = state.progress;
    const percent = Math.min(100, Math.round((done / Math.max(1, total)) * 100));
    return (
      <div className="offline-card" aria-live="polite">
        <div className="offline-card__row">
          <span className="ds-text-subheadline">{t("offline.saving", { percent })}</span>
          <Button variant="tinted" onClick={() => abort.current?.abort()}>
            {t("offline.cancel")}
          </Button>
        </div>
        <div
          role="progressbar"
          aria-label={t("offline.progress")}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
          className="offline-progress"
        >
          <span style={{ width: `${percent}%` }} />
        </div>
      </div>
    );
  }

  if (saved && state.kind !== "error") {
    return (
      <div className="offline-card">
        <div className="offline-card__row">
          <span className="ds-text-subheadline offline-card__ok">
            <CircleCheck aria-hidden="true" size={20} strokeWidth={1.75} />
            {t("offline.saved")} · {formatBytes(saved.bytes, locale)}
          </span>
          <Button
            variant="tinted"
            aria-label={t("offline.removeNamed", { name: spot.name })}
            onClick={() => void removeSpot(spot.slug, { store: idbStore })}
          >
            {t("offline.remove")}
          </Button>
        </div>
      </div>
    );
  }

  if (state.kind === "confirm") {
    return (
      <div className="offline-card">
        <p className="ds-text-subheadline m-0">
          {t("offline.estimate", { size: formatBytes(estimate.bytes, locale) })}
        </p>
        <div className="offline-card__row">
          <Button variant="filled" onClick={() => void start()}>
            {t("offline.confirm")}
          </Button>
          <Button variant="tinted" onClick={() => setState({ kind: "idle" })}>
            {t("offline.cancel")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="grid gap-2 justify-items-start">
      <Button
        variant="tinted"
        icon={<CloudDownload aria-hidden="true" strokeWidth={1.75} />}
        onClick={() => setState({ kind: "confirm" })}
      >
        {t("offline.save")}
      </Button>
      {state.kind === "error" ? (
        <p className="ds-text-footnote offline-card__err m-0" role="alert">
          <TriangleAlert aria-hidden="true" size={16} strokeWidth={1.75} />
          {t(`offline.error.${state.error}`)}
        </p>
      ) : null}
    </div>
  );
}
